import React, { useEffect, useState } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  Calendar,
  Users,
  BarChart2,
  Tag,
  Eye,
  Power,
  PowerOff,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '@/components/ui/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';

const COLORS = {
  orange: '#E87E04',
  green: '#008751',
  darkGreen: '#006B3F',
  lightOrange: '#FFE0B2',
  gray: '#718096',
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

export default function PromoCodes() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [filteredPromoCodes, setFilteredPromoCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterPromoCodes();
  }, [promoCodes, searchTerm, statusFilter]);

  const admin = JSON.parse(localStorage.getItem('AdminEcomme'));
  if (admin) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${admin.token}`;
  }

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [promoCodesRes, statsRes] = await Promise.all([
        axios.get(`${BackendUrl}/api/promocodes/admin`, {
          withCredentials: true,
        }),
        axios.get(`${BackendUrl}/api/promocodes/admin/stats/global`, {
          withCredentials: true,
        }),
      ]);

      setPromoCodes(promoCodesRes.data.promoCodes || []);
      setStats(statsRes.data.stats || {});
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur de chargement',
        description: error.response?.data?.message || 'Impossible de charger les codes promo. Vérifiez votre connexion.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterPromoCodes = () => {
    let filtered = promoCodes;

    // Filtre de recherche
    if (searchTerm) {
      filtered = filtered.filter((promo) =>
        promo.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre de statut
    const now = new Date();
    if (statusFilter === 'active') {
      filtered = filtered.filter(
        (promo) => promo.isActive && new Date(promo.endDate) >= now
      );
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter((promo) => !promo.isActive);
    } else if (statusFilter === 'expired') {
      filtered = filtered.filter((promo) => new Date(promo.endDate) < now);
    }

    setFilteredPromoCodes(filtered);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${BackendUrl}/api/promocodes/admin/${deleteId}`, {
        withCredentials: true,
      });

      toast({
        title: 'Succès',
        description: 'Code promo supprimé avec succès',
      });

      fetchData();
      setDeleteId(null);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur de suppression',
        description: error.response?.data?.message || 'Impossible de supprimer le code promo. Il est peut-être utilisé dans des commandes actives.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await axios.patch(
        `${BackendUrl}/api/promocodes/admin/${id}/toggle`,
        {},
        { withCredentials: true }
      );

      toast({
        title: 'Succès',
        description: `Code promo ${currentStatus ? 'désactivé' : 'activé'} avec succès`,
      });

      fetchData();
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur de modification',
        description: error.response?.data?.message || 'Impossible de modifier le statut du code promo.',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (promo) => {
    const now = new Date();
    const endDate = new Date(promo.endDate);

    if (!promo.isActive) {
      return <Badge variant="secondary">Inactif</Badge>;
    }

    if (endDate < now) {
      return <Badge variant="destructive">Expiré</Badge>;
    }

    if (promo.maxUsage && promo.currentUsage >= promo.maxUsage) {
      return <Badge variant="destructive">Épuisé</Badge>;
    }

    return <Badge style={{ backgroundColor: COLORS.green }}>Actif</Badge>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatValue = (promo) => {
    if (promo.type === 'percentage') {
      return `${promo.value}%`;
    }
    return `${promo.value} €`;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-10 w-64 mb-6" />
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Codes Promo</h1>
          <p className="text-gray-600">Gérez vos codes de réduction</p>
        </div>
        <Link to="/Admin/AddPromoCode">
          <Button style={{ backgroundColor: COLORS.orange }}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau Code
          </Button>
        </Link>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Codes
              </CardTitle>
              <Tag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCodes}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Codes Actifs</CardTitle>
              <Power className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.activeCodes}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Utilisations
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsage}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Réductions Totales
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.totalDiscountGiven?.toLocaleString('fr-FR')} €
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher un code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
                <SelectItem value="expired">Expirés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Valeur</TableHead>
                <TableHead>Utilisations</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPromoCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Aucun code promo trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredPromoCodes.map((promo) => (
                  <TableRow key={promo._id}>
                    <TableCell className="font-medium">{promo.code}</TableCell>
                    <TableCell>
                      {promo.type === 'percentage' ? 'Pourcentage' : 'Montant fixe'}
                    </TableCell>
                    <TableCell>{formatValue(promo)}</TableCell>
                    <TableCell>
                      {promo.currentUsage}
                      {promo.maxUsage ? ` / ${promo.maxUsage}` : ' / ∞'}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{formatDate(promo.startDate)}</div>
                        <div className="text-gray-500">
                          → {formatDate(promo.endDate)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(promo)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/Admin/PromoCodeDetail/${promo._id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={`/Admin/EditPromoCode/${promo._id}`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleToggleStatus(promo._id, promo.isActive)
                          }
                        >
                          {promo.isActive ? (
                            <PowerOff className="h-4 w-4 text-red-600" />
                          ) : (
                            <Power className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteId(promo._id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce code promo ? Cette action
              est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              style={{ backgroundColor: COLORS.orange }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
